import React, { useState, useEffect } from 'react';
import { Table, Typography, Select, Spin, Button, DatePicker, Space, message, Tooltip, Tag, Skeleton, Empty, Badge, Card, Row, Col, Alert } from 'antd';
import { DownOutlined, RightOutlined, CloudOutlined, DownloadOutlined, ThunderboltOutlined, SearchOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import styles from './HistoricalWeather.module.css';
import api from '../../../authentication/axiosInstance';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { Title, Text } = Typography;
const { Option } = Select;

// Weather station options
const WEATHER_STATIONS = [
  { id: '51459', name: 'Toronto INTL A', province: 'ON' },
  { id: '51157', name: 'Montreal/Pierre Elliott Trudeau Intl', province: 'QC' },
  { id: '50089', name: 'Ottawa Macdonald-Cartier Int\'l', province: 'ON' },
  { id: '51442', name: 'Quebec/Jean Lesage Intl', province: 'QC' },
  { id: '27211', name: 'Calgary Int\'l', province: 'AB' },
  { id: '50821', name: 'Winnipeg Richardson Int\'l', province: 'MB' },
  { id: '51337', name: 'Vancouver Int\'l', province: 'BC' },
];

const HistoricalWeather = () => {
  const [weatherData, setWeatherData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stationId, setStationId] = useState('51459');  // Toronto City station ID
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(2, 'month'),
    dayjs()
  ]);
  const [dataType, setDataType] = useState('daily');
  const [expandedRows, setExpandedRows] = useState([]);
  const [hourlyData, setHourlyData] = useState({});
  const [storms, setStorms] = useState([]);
  const [loadingStorms, setLoadingStorms] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchedStation, setFetchedStation] = useState(null);
  const [selectedStorm,setSelectedStorm] = useState("All");

  useEffect(() => {
    const fetchInitialData = async () => {
      await Promise.all([fetchWeatherData(), fetchStorms()]);
      setInitialLoading(false);
    };
    
    fetchInitialData();
  }, []);

  const fetchStorms = async () => {
    setLoadingStorms(true);
    setError(null);
    try {
      const response = await api.get('/dispatch/storm/');
      if (response.data) {
        setStorms(response.data);
      } else {
        setStorms([]);
      }
    } catch (error) {
      console.error('Error fetching storm data:', error);
      message.error('Failed to fetch storm data');
      setStorms([]);
      setError('Failed to load storm data. Please try again later.');
    } finally {
      setLoadingStorms(false);
    }
  };

  const fetchWeatherData = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      message.error('Please select a valid date range');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      
      const apiUrl = `/debrief/weather-data/${stationId}/${startDate}/${endDate}/${dataType}`;
      
      const response = await api.get(apiUrl);
      
      // Basic logging - not excessive
      console.log('API URL:', apiUrl);
      
      if (response.data && response.data.features && response.data.features.length > 0) {
        // Log available properties only on first run
        if (!fetchedStation) {
          console.log('Available properties:', Object.keys(response.data.features[0].properties));
        }
        
        const formattedData = response.data.features.map(feature => {
          const props = feature.properties;
          
          // Direct access to the specific fields we know exist
          const snowfallValue = props.TOTAL_SNOW;
          const snowOnGroundValue = props.SNOW_ON_GROUND;
          
          return {
            key: props.LOCAL_DATE,
            date: props.LOCAL_DATE,
            maxTemp: props.MAXIMUM_TEMPERATURE,
            minTemp: props.MINIMUM_TEMPERATURE,
            meanTemp: props.MEAN_TEMPERATURE,
            precipitation: props.TOTAL_PRECIPITATION,
            snowfall: snowfallValue,
            snowOnGround: snowOnGroundValue,
            stationName: props.STATION_NAME,
            province: props.PROVINCE_CODE
          };
        });

        setWeatherData(formattedData);
        
        // Save the current station info
        const stationInfo = WEATHER_STATIONS.find(s => s.id === stationId);
        setFetchedStation(stationInfo ? stationInfo.name : formattedData[0]?.stationName || 'Selected Station');
      } else {
        message.warning('No weather data available for the selected criteria');
        setWeatherData([]);
      }
    } catch (error) {
      console.error('Error fetching weather data:', error);
      message.error('Failed to fetch weather data. Please try again.');
      setWeatherData([]);
      setError('Failed to load weather data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHourlyData = async (date) => {
    if (hourlyData[date]) return;
    
    setLoading(true);
    try {
      // Format date to YYYY-MM-DD
      const formattedDate = dayjs(date).format('YYYY-MM-DD');
      const apiUrl = `/debrief/weather-data/${stationId}/${formattedDate}/${formattedDate}/hourly`;
      
      const response = await api.get(apiUrl);
      
      if (response.data && response.data.features && response.data.features.length > 0) {
        const hourlyEntries = response.data.features.map(feature => {
          const props = feature.properties;
          const temp = parseFloat(props.TEMP);
          const dewPoint = parseFloat(props.DEW_POINT_TEMP);
          
          // Calculate relative humidity using temperature and dew point
          const relativeHumidity = temp !== null && dewPoint !== null ? 
            100 * Math.exp((17.625 * dewPoint)/(243.04 + dewPoint)) / Math.exp((17.625 * temp)/(243.04 + temp)) :
            null;
          
          return {
            key: `${props.LOCAL_DATE}-${props.LOCAL_HOUR}`,
            hour: props.LOCAL_HOUR,
            temperature: temp,
            dewPoint: dewPoint,
            relativeHumidity: relativeHumidity,
            precipitation: props.PRECIP_AMOUNT
          };
        });
        
        // Sort by hour to ensure they display in chronological order
        hourlyEntries.sort((a, b) => a.hour - b.hour);
        
        setHourlyData(prev => ({
          ...prev,
          [date]: hourlyEntries
        }));
      } else {
        message.warning(`No hourly weather data available for ${dayjs(date).format('MMM DD, YYYY')}`);
        setHourlyData(prev => ({
          ...prev,
          [date]: []
        }));
      }
    } catch (error) {
      console.error('Error fetching hourly data:', error);
      message.error('Failed to fetch hourly weather data');
      setHourlyData(prev => ({
        ...prev,
        [date]: []
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (date) => {
    if (date) {
      const newStart = dayjs(date).startOf('day');
      const currentEnd = dateRange[1];
      
      // Make sure end date is not before start date
      if (currentEnd.isBefore(newStart)) {
        setDateRange([newStart, newStart.add(1, 'month')]);
      } else {
        setDateRange([newStart, currentEnd]);
      }
    }
  };

  const handleEndDateChange = (date) => {
    if (date) {
      const currentStart = dateRange[0];
      const newEnd = dayjs(date).endOf('day');
      
      // Make sure start date is not after end date
      if (currentStart.isAfter(newEnd)) {
        setDateRange([newEnd.subtract(1, 'month'), newEnd]);
      } else {
        setDateRange([currentStart, newEnd]);
      }
    }
  };

  const handleStationChange = (value) => {
    setStationId(value);
  };

  const handleStormSelect = (value) => {
    setSelectedStorm(value)
    if (value !== "All") {
      const storm = storms.find(storm => storm.storm_id === value);
      const startDate = dayjs(storm.date_of_storm);
      const endDate = dayjs(storm.date_of_storm_end);

      setDateRange([startDate, endDate]);
    }
    else 
      setDateRange([
        dayjs().subtract(2, 'month'),
        dayjs()
      ])
  }

  const handleSearch = () => {
    fetchWeatherData();
  };

  const handleRefresh = () => {
    fetchStorms();
    fetchWeatherData();
  };

  const handleRowExpand = async (expanded, record) => {
    if (expanded) {
      await fetchHourlyData(record.date);
      setExpandedRows(prev => [...prev, record.key]);
    } else {
      setExpandedRows(prev => prev.filter(key => key !== record.key));
    }
  };

  const downloadCSV = () => {
    if (!weatherData.length) {
      message.warning('No data to download');
      return;
    }

    // Create CSV content
    const headers = [
      'Date', 'Station', 'Temperature (°C)', 'Precipitation (mm)', 
      'Snowfall (cm)', 'Snow on Ground (cm)', 'Storm Event'
    ];
    
    const csvContent = [
      headers.join(','),
      ...weatherData.map(item => {
        const stormDay = checkIfDateHadStorm(item.date);
        return [
          item.date,
          item.stationName,
          item.meanTemp,
          item.precipitation,
          item.snowfall,
          item.snowOnGround,
          stormDay ? `${stormDay.type} (${stormDay.severity})` : 'No'
        ].join(',');
      })
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    
    const stationNameForFile = fetchedStation ? 
      fetchedStation.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 
      'weather_data';
    
    link.setAttribute('download', `weather_data_${stationNameForFile}_${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('Data export successful!');
  };

  // Helper function to check if a date had a storm
  const checkIfDateHadStorm = (dateString) => {
    const date = dayjs(dateString);
    // Check if the date falls between any storm's start and end dates
    return storms.find(storm => {
      const stormStart = dayjs(storm.date_of_storm);
      const stormEnd = dayjs(storm.date_of_storm_end);
      return date.isSameOrAfter(stormStart, 'day') && date.isSameOrBefore(stormEnd, 'day');
    });
  };

  // Get severity class for coloring
  const getSeverityClass = (severity) => {
    switch(severity) {
      case 'light': return styles.lightStorm;
      case 'medium': return styles.mediumStorm;
      case 'heavy': return styles.heavyStorm;
      default: return '';
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      fixed: 'left',
      width: 180,
      render: date => {
        const storm = checkIfDateHadStorm(date);
        return (
          <Space>
            {dayjs(date).format('MMM DD, YYYY')}
            {storm && (
              <Tooltip title={
                <div>
                  <p><strong>Storm Type:</strong> {storm.type || 'Unknown'}</p>
                  <p><strong>Severity:</strong> {storm.severity || 'Unknown'}</p>
                  <p><strong>From:</strong> {dayjs(storm.date_of_storm).format('MMM DD, YYYY HH:mm')}</p>
                  <p><strong>To:</strong> {dayjs(storm.date_of_storm_end).format('MMM DD, YYYY HH:mm')}</p>
                </div>
              }>
                <Tag className={`${styles.stormTag} ${getSeverityClass(storm.severity)}`}>
                  <ThunderboltOutlined /> STORM
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
      sorter: (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
    },
    {
      title: 'Station',
      dataIndex: 'stationName',
      key: 'stationName',
      width: 150,
    },
    {
      title: (
        <span>
          Temperature (°C)
          <Tooltip title="Mean temperature for the day">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'meanTemp',
      key: 'meanTemp',
      width: 140,
      render: temp => {
        if (temp === null) return 'N/A';
        const value = parseFloat(temp);
        return <span>{value.toFixed(1)}</span>;
      },
      sorter: (a, b) => a.meanTemp - b.meanTemp,
    },
    {
      title: (
        <span>
          Precipitation (mm)
          <Tooltip title="Total precipitation including rain and melted snow">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'precipitation',
      key: 'precipitation',
      width: 150,
      render: value => {
        if (value === null) return '0.0';
        const numValue = parseFloat(value);
        return (
          <span className={numValue > 10 ? styles.highPrecip : numValue > 0 ? styles.mediumPrecip : ''}>
            {numValue.toFixed(1)}
          </span>
        );
      },
      sorter: (a, b) => a.precipitation - b.precipitation,
    },
    {
      title: (
        <span>
          Snowfall (cm)
          <Tooltip title="Total snowfall for the day">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'snowfall',
      key: 'snowfall',
      width: 120,
      render: value => {
        if (value === null || value === undefined || value === '') return '0.0';
        // Try to parse as float, but handle potential errors
        let numValue = 0;
        try {
          numValue = parseFloat(value);
          if (isNaN(numValue)) numValue = 0;
        } catch (e) {
          numValue = 0;
        }
        return (
          <span className={numValue > 5 ? styles.highSnow : numValue > 0 ? styles.mediumSnow : ''}>
            {numValue.toFixed(1)}
          </span>
        );
      },
      sorter: (a, b) => {
        const aVal = a.snowfall === null || a.snowfall === undefined ? 0 : parseFloat(a.snowfall);
        const bVal = b.snowfall === null || b.snowfall === undefined ? 0 : parseFloat(b.snowfall);
        return aVal - bVal;
      },
    },
    {
      title: 'Snow on Ground (cm)',
      dataIndex: 'snowOnGround',
      key: 'snowOnGround',
      width: 170,
      render: value => value !== null ? parseFloat(value).toFixed(1) : '0.0',
      sorter: (a, b) => a.snowOnGround - b.snowOnGround,
    },
  ];

  const hourlyColumns = [
    {
      title: 'Hour',
      dataIndex: 'hour',
      key: 'hour',
      render: hour => `${hour}:00`,
      width: 80,
      fixed: 'left'
    },
    {
      title: (
        <span>
          Temperature
          <Tooltip title="Temperature in °C">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'temperature',
      key: 'temperature',
      render: temp => {
        if (temp === null) return 'N/A';
        const value = parseFloat(temp);
        return <span>{value.toFixed(1)}</span>;
      },
      width: 140,
    },
    {
      title: (
        <span>
          Dew Point
          <Tooltip title="Dew Point in °C">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'dewPoint',
      key: 'dewPoint',
      render: value => value !== null ? parseFloat(value).toFixed(1) : 'N/A',
      width: 140,
    },
    {
      title: (
        <span>
          Relative Humidity
          <Tooltip title="Relative Humidity in %">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'relativeHumidity',
      key: 'relativeHumidity',
      render: value => value !== null ? parseFloat(value).toFixed(0) : 'N/A',
      width: 160,
    },
    {
      title: (
        <span>
          Precipitation
          <Tooltip title="Precipitation Amount in mm">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'precipitation',
      key: 'precipitation',
      render: value => {
        if (value === null) return '0.0';
        const numValue = parseFloat(value);
        return (
          <span className={numValue > 10 ? styles.highPrecip : numValue > 0 ? styles.mediumPrecip : ''}>
            {numValue.toFixed(1)}
          </span>
        );
      },
      width: 140,
    }
  ];

  const expandedRowRender = (record) => {
    const data = hourlyData[record.date] || [];
    
    if (!data.length) {
      return <Spin tip="Loading hourly data..." />;
    }
    
    return (
      <div className={styles.expandedRowContent}>
        <div className={styles.hourlyHeader}>
          <Text strong>
            {dayjs(record.date).format('MMMM DD, YYYY')} - Hourly Data ({data.length} hours)
          </Text>
        </div>
        <Table 
          columns={hourlyColumns} 
          dataSource={data} 
          pagination={false} 
          size="small"
          className={styles.hourlyTable}
          locale={{ emptyText: 'No hourly data available for this date' }}
          scroll={{ x: 1000 }}
        />
      </div>
    );
  };

  if (initialLoading) {
    return (
      <div className={styles.container}>
        <Skeleton active paragraph={{ rows: 1 }} />
        <div style={{ margin: '24px 0' }}>
          <Skeleton.Button active size="large" shape="default" style={{ width: 150, marginRight: 16 }} />
          <Skeleton.Button active size="large" shape="default" style={{ width: 150, marginRight: 16 }} />
          <Skeleton.Button active size="large" shape="default" style={{ width: 100 }} />
        </div>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={2} className={styles.pageTitle}>
              <CloudOutlined /> Historical Weather Data
            </Title>
            <Text className={styles.subheading}>
              View and analyze historical weather conditions with storm indicators
            </Text>
          </Col>
          <Col>
            <Tooltip title="Refresh all data">
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
                loading={loading || loadingStorms}
                className={styles.refreshButton}
              >
                Refresh
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </div>

      <Card className={styles.filterCard}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={6} lg={5}>
            <Text strong className={styles.filterLabel}>Weather Station:</Text>
            <Select
              value={stationId}
              onChange={handleStationChange}
              className={styles.stationSelect}
              disabled={loading}
              showSearch
              optionFilterProp="children"
            >
              {WEATHER_STATIONS.map(station => (
                <Option key={station.id} value={station.id}>
                  {station.name} ({station.province})
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={6} lg={5}>
            <Text strong className={styles.filterLabel}>Filter by Storm:</Text>
            <Select
              value={selectedStorm}
              onChange={handleStormSelect}
              className={styles.stormSelect}
              disabled={loading}
              showSearch
              optionFilterProp="children"
            >
              <Option value="All">All</Option>
              {storms.map(storm => (
                <Option key={storm.storm_id} value={storm.storm_id}>
                 {`Type: ${storm.type && (storm.type).split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} - ${storm.date_of_storm ? dayjs(storm.date_of_storm).format('MMMM Do, YYYY') : 'N/A'} - ID: ${storm.storm_id}`}
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col xs={24} md={8} lg={8}>
            <Text strong className={styles.filterLabel}>Date Range:</Text>
            <div className={styles.datePickerContainer}>
              <DatePicker
                value={dateRange[0]}
                onChange={handleStartDateChange}
                format="YYYY-MM-DD"
                placeholder="Start Date"
                className={styles.singleDatePicker}
                disabled={loading}
                disabledDate={current => current && current > dayjs().endOf('day')}
              />
              <span className={styles.dateRangeSeparator}>to</span>
              <DatePicker
                value={dateRange[1]}
                onChange={handleEndDateChange}
                format="YYYY-MM-DD"
                placeholder="End Date"
                className={styles.singleDatePicker}
                disabled={loading}
                disabledDate={current => current && current > dayjs().endOf('day')}
              />
            </div>
          </Col>
          
          <Col xs={12} md={2} lg={3}>
            <Text strong className={styles.filterLabel}>Data Type:</Text>
            <Select
              value={dataType}
              onChange={setDataType}
              className={styles.typeSelect}
              disabled={loading}
            >
              <Option value="daily">Daily</Option>
              <Option value="monthly">Monthly</Option>
            </Select>
          </Col>
          
          <Col xs={12} md={2} lg={3}>
            <Button 
              type="primary" 
              icon={<SearchOutlined />}
              onClick={handleSearch} 
              loading={loading}
              className={styles.searchButton}
            >
              Search
            </Button>
          </Col>
        </Row>
        
        {fetchedStation && (
          <div className={styles.currentQuery}>
            <InfoCircleOutlined className={styles.infoIcon} />
            <Text type="secondary">
              Showing data for <strong>{fetchedStation}</strong> from {dateRange[0].format('MMM DD, YYYY')} to {dateRange[1].format('MMM DD, YYYY')}
            </Text>
          </div>
        )}
      </Card>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          className={styles.errorAlert}
          closable
        />
      )}

      {weatherData.length === 0 && !loading && !error && (
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div className={styles.emptyDescription}>
              <p>No weather data found for the selected period and station.</p>
              <p>Try selecting a different date range or weather station.</p>
              <p><small>Note: Weather data is typically available with a delay and future dates will not have data.</small></p>
            </div>
          }
          className={styles.emptyState}
        />
      )}

      {(weatherData.length > 0 || loading) && (
        <Card className={styles.tableCard}>
          <div className={styles.tableActions}>
            <div className={styles.legendContainer}>
              <Text strong>Legend: </Text>
              <Tag className={`${styles.legendItem} ${styles.stormTag}`}>
                <ThunderboltOutlined /> Storm Event
              </Tag>
              <Tag className={`${styles.legendItem} ${styles.highPrecip}`}>Heavy Precip</Tag>
              <Tag className={`${styles.legendItem} ${styles.highSnow}`}>Heavy Snow</Tag>
            </div>
            
            <Tooltip title="Download as CSV">
              <Button 
                icon={<DownloadOutlined />} 
                onClick={downloadCSV}
                disabled={!weatherData.length || loading}
                className={styles.downloadButton}
              >
                Export Data
              </Button>
            </Tooltip>
          </div>
          
          <Table
            columns={columns}
            dataSource={weatherData}
            loading={loading || loadingStorms}
            expandable={{
              expandedRowRender,
              expandedRowKeys: expandedRows,
              onExpand: handleRowExpand,
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <DownOutlined 
                    onClick={e => onExpand(record, e)} 
                    className={styles.expandIcon}
                  />
                ) : (
                  <RightOutlined 
                    onClick={e => onExpand(record, e)} 
                    className={styles.expandIcon}
                  />
                )
            }}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records` 
            }}
            className={styles.dataTable}
            rowClassName={(record) => {
              const stormDay = checkIfDateHadStorm(record.date);
              return stormDay 
                ? `${styles.tableRow} ${styles.stormRow} ${getSeverityClass(stormDay.severity)}` 
                : styles.tableRow;
            }}
            bordered={true}
            size="middle"
            scroll={{ x: 1100 }}
          />
        </Card>
      )}
    </div>
  );
};

export default HistoricalWeather;
